import React, { useState, useRef, useEffect } from "react";
import "./login.css";
import { _post } from "../services/apiClient";
import { jwtDecode } from "jwt-decode";

export default function LoginSignup() {
  const [isSignup, setIsSignup] = useState(false);
  const [mobile, setMobile] = useState("");
  const [signup, setSignup] = useState({ name: "", mobile: "", email: "" });
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isOtpScreen, setIsOtpScreen] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);
  const [timer, setTimer] = useState(0);
  const [pendingSignup, setPendingSignup] = useState(null); // Store signup details for OTP verification

  // Auto-clear messages after 3 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message.text]);

  // Countdown timer for Resend OTP
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleClose = () => {
    window.location = "/";
  };

  const mobileRegex = /^[0-9]{10}$/;
  const emailRegex =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Signup handler (send OTP, do not create user yet)
  const handleSignup = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    setLoading(true);

    if (!mobileRegex.test(signup.mobile)) {
      setMessage({ type: "error", text: "Enter a valid 10-digit mobile number" });
      setLoading(false);
      return;
    }
    if (signup.email && !emailRegex.test(signup.email)) {
      setMessage({ type: "error", text: "Enter a valid email address" });
      setLoading(false);
      return;
    }

    try {
      // Send OTP for signup, do not create user yet
      const res = await _post("/auth/userSignupOtp", {
        name: signup.name,
        phoneNumber: signup.mobile,
        email: signup.email,
      });

      if (res.data.success) {
        setMessage({ type: "success", text: "OTP sent successfully." });
        setIsOtpScreen(true);
        setTimer(120);
        setPendingSignup({ ...signup }); // Store details for OTP verification
        setMobile(signup.mobile); // For OTP screen
      } else {
        setMessage({ type: "error", text: res.data.message || "Signup failed" });
      }
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || "Something went wrong during signup" });
    } finally {
      setLoading(false);
    }
  };

  // Login handler (unchanged)
  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    setLoading(true);

    if (!mobileRegex.test(mobile)) {
      setMessage({ type: "error", text: "Enter a valid 10-digit mobile number" });
      setLoading(false);
      return;
    }

    try {
      const res = await _post("/auth/userLogin", { phoneNumber: mobile });

      if (res.data.success) {
        setMessage({ type: "success", text: "OTP sent successfully." });
        setIsOtpScreen(true);
        setTimer(120);
      } else {
        setMessage({ type: "error", text: res.data.message || "Login failed" });
      }
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || "Something went wrong during login" });
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP handler (for signup or login)
  const handleResendOtp = async () => {
    setLoading(true);
    try {
      if (isSignup && pendingSignup) {
        // Resend OTP for signup
        const res = await _post("/auth/userSignupOtp", {
          name: pendingSignup.name,
          phoneNumber: pendingSignup.mobile,
          email: pendingSignup.email,
        });
        if (res.data.success) {
          setMessage({ type: "success", text: "OTP resent successfully." });
          setTimer(120);
        } else {
          setMessage({ type: "error", text: res.data.message || "Resend failed" });
        }
      } else {
        const res = await _post("/auth/userLogin", { phoneNumber: mobile });
        if (res.data.success) {
          setMessage({ type: "success", text: "OTP resent successfully." });
          setTimer(120);
        } else {
          setMessage({ type: "error", text: res.data.message || "Resend failed" });
        }
      }
    } catch (err) {
      setMessage({ type: "error", text: "Something went wrong while resending OTP" });
    } finally {
      setLoading(false);
    }
  };

  // OTP input change handler
  const handleOtpChange = (e, index) => {
    const val = e.target.value.replace(/\D/g, "");

    if (val.length > 1) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = val[i] || "";
      }
      setOtp(newOtp);

      const otpValue = newOtp.join("");
      if (otpValue.length === 6 && !loading) {
        handleVerifyOtpAuto(otpValue);
      }
    } else {
      setOtp((prev) => {
        const newOtp = [...prev];
        newOtp[index] = val[0] || "";

        const otpValue = newOtp.join("");
        if (otpValue.length === 6 && !loading) {
          handleVerifyOtpAuto(otpValue);
        }

        return newOtp;
      });

      if (val && index < 5) inputRefs.current[index + 1].focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("Text").replace(/\D/g, "").slice(0, 6);
    const newOtp = Array(6).fill("");
    for (let i = 0; i < pasteData.length; i++) newOtp[i] = pasteData[i];
    setOtp(newOtp);

    if (pasteData.length === 6) handleVerifyOtpAuto(pasteData);
  };


  // Auto-submit OTP when all 6 digits are entered
  const handleVerifyOtpAuto = async (otpValue) => {
    setLoading(true);
    try {
      if (isSignup && pendingSignup) {
        const res = await _post("/auth/userSignup", {
          name: pendingSignup.name,
          phoneNumber: pendingSignup.mobile,
          email: pendingSignup.email,
          otp: otpValue,
        });

        if (res.data.success) {
          localStorage.setItem("token", res.data.token);
          const decoded = jwtDecode(res.data.token);
          setMessage({ type: "success", text: "Signup successful. Redirecting..." });
          setTimeout(() => (window.location = "/"), 1200);
        } else {
          setMessage({ type: "error", text: res.data.message || "OTP verification failed" });
        }
      } else {
        // Login OTP verification
        const res = await _post("/auth/verifyOtp", { phoneNumber: mobile, otp: otpValue });

        if (res.data.success) {
          localStorage.setItem("token", res.data.token);
          const decoded = jwtDecode(res.data.token);
          setMessage({ type: "success", text: "Login successful Redirecting..." });
          setTimeout(() => (window.location = "/"), 1200);
        } else {
          setMessage({ type: "error", text: res.data.message || "OTP verification failed" });
        }
      }
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || "Something went wrong during OTP verification" });
    } finally {
      setLoading(false);
    }
  };

  // Handle backspace to move focus
  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length < 6) {
      setMessage({ type: "error", text: "Enter complete 6-digit OTP" });
      return;
    }

    setLoading(true);
    try {
      if (isSignup && pendingSignup) {
        const res = await _post("/auth/userSignup", {
          name: pendingSignup.name,
          phoneNumber: pendingSignup.mobile,
          email: pendingSignup.email,
          otp: otpValue,
        });

        if (res.data.success) {
          localStorage.setItem("token", res.data.token);
          const decoded = jwtDecode(res.data.token);
          setMessage({ type: "success", text: "Signup successful. Redirecting..." });
          setTimeout(() => (window.location = "/"), 1200);
        } else {
          setMessage({ type: "error", text: res.data.message || "OTP verification failed" });
        }
      } else {
        const res = await _post("/auth/verifyOtp", { phoneNumber: mobile, otp: otpValue });

        if (res.data.success) {
          localStorage.setItem("token", res.data.token);
          const decoded = jwtDecode(res.data.token);
          setMessage({ type: "success", text: "Login successful Redirecting..." });
          setTimeout(() => (window.location = "/"), 1200);
        } else {
          setMessage({ type: "error", text: res.data.message || "OTP verification failed" });
        }
      }
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || "Something went wrong during OTP verification" });
    } finally {
      setLoading(false);
    }
  };

  // Auto-focus first OTP input when screen changes
  useEffect(() => {
    if (isOtpScreen) inputRefs.current[0].focus();
  }, [isOtpScreen]);

  return (
    <div className="ls-split-root">

      <div className="ls-left-panel">
        <div className="ls-info-content">
          <img
            src="https://cdn-icons-png.flaticon.com/512/702/702797.png"
            alt="Business illustration"
            className="ls-info-illustration"
          />
          <h2>
            Let's Combine <br />
            Our Experiences & Make Your<br />
            <b>BUSINESS PROCUREMENT</b> Simpler
          </h2>
          <div className="ls-feature-list">
            <div>
              <b>8K+</b>
              <div>Global Brands</div>
            </div>
            <div>
              <b>20L+</b>
              <div>Certified Products</div>
            </div>
            <div>
              <b>24Hrs</b>
              <div>Shipment</div>
            </div>
            <div>
              <b>40 Lakh+</b>
              <div>Business Customers</div>
            </div>
            <div>
              <b>₹5Lakh</b>
              <div>IB Credit</div>
            </div>
            <div>
              <b>19K+</b>
              <div>Delivery Pincodes</div>
            </div>
          </div>
        </div>
      </div>

      <div className="ls-form-panel">
        <button className="ls-close" onClick={handleClose} aria-label="Close panel">
          ×
        </button>
        <div className="ls-form-content">
          <div className="ls-avatar">
            <img src="https://cdn-icons-png.flaticon.com/512/9131/9131529.png" alt="avatar" />
          </div>
          <h2 className="ls-form-title">
            {isOtpScreen ? "Enter OTP" : isSignup ? "Sign Up" : "Login"}
          </h2>

          {!isOtpScreen ? (
            !isSignup ? (
              <form className="ls-form" onSubmit={handleLogin}>
                <input
                  type="tel"
                  placeholder="Enter Mobile Number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  required
                  maxLength="10"
                  className="ls-input"
                />
                <button type="submit" className="ls-btn" disabled={loading}>
                  {loading ? <span className="spinner"></span> : "Login"}
                </button>

              </form>
            ) : (
              <form className="ls-form" onSubmit={handleSignup}>
                <input
                  type="text"
                  placeholder="Full Name"
                  value={signup.name}
                  onChange={(e) => setSignup({ ...signup, name: e.target.value })}
                  required
                  className="ls-input"
                />
                <input
                  type="tel"
                  placeholder="Mobile Number"
                  value={signup.mobile}
                  onChange={(e) =>
                    setSignup({ ...signup, mobile: e.target.value.replace(/\D/g, "") })
                  }
                  required
                  maxLength="10"
                  className="ls-input"
                />
                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={signup.email}
                  onChange={(e) => setSignup({ ...signup, email: e.target.value })}
                  pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                  className="ls-input"
                />
                <button type="submit" className="ls-btn" disabled={loading}>
                  {loading ? <span className="spinner"></span> : "Create Account"}
                </button>
              </form>
            )
          ) : (
            // OTP Screen
            <form className="ls-form" onSubmit={handleVerifyOtp}>

              <div style={{ marginBottom: "10px", textAlign: "center" }}>
                <span style={{ fontSize: "14px", color: "#333" }}>
                  OTP sent to <b> {isSignup && pendingSignup ? pendingSignup.mobile : mobile}</b>
                </span>
                <br />
                <span
                  className="ls-link"
                  style={{ cursor: "pointer", color: "#007bff", fontSize: "13px" }}
                  onClick={() => {
                    setIsOtpScreen(false);
                    setOtp(Array(6).fill(""));
                  }}
                >
                  Change number
                </span>
              </div>

              <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginBottom: "15px" }}>
                {otp.map((val, idx) => (
                  <input
                    key={idx}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="1"
                    value={val}
                    onChange={(e) => handleOtpChange(e, idx)}
                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                    onPaste={handleOtpPaste}
                    ref={(el) => (inputRefs.current[idx] = el)}
                    className="ls-input"
                    style={{ width: "40px", textAlign: "center", fontSize: "1.2rem" }}
                  />

                ))}
              </div>

              <div style={{ marginTop: "12px", textAlign: "center" }}>
                {timer > 0 ? (
                  <span style={{ color: "#666" }}>Resend OTP in {timer}s</span>
                ) : (
                  <span
                    className="ls-link"
                    onClick={handleResendOtp}
                    style={{ cursor: "pointer", color: "#007bff" }}
                  >
                    Resend OTP
                  </span>
                )}
              </div>
            </form>
          )}

          {message.text && (
            <div
              className={`ls-message ${message.type === "error" ? "ls-error" : "ls-success"}`}
            >
              {message.text}
            </div>
          )}

          <div className="ls-switch-link">
            {!isOtpScreen &&
              (!isSignup ? (
                <>
                  Don't have an account?{" "}
                  <span className="ls-link" onClick={() => setIsSignup(true)}>
                    Create account
                  </span>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <span className="ls-link" onClick={() => setIsSignup(false)}>
                    Login
                  </span>
                </>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}